import { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Wallet,
  MoreHorizontal,
  Loader2,
  KeyRound,
  History,
  Ban,
  CheckCircle,
  Trash2,
  User,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import { useCustomerAccounts, type CustomerAccount } from '@/hooks/useCustomerAccounts';
import { toast } from 'sonner';
import { CreateCustomerAccountDialog } from './CreateCustomerAccountDialog';
import { WalletTopUpDialog } from './WalletTopUpDialog';
import { WalletTransactionsDialog } from './WalletTransactionsDialog';

const statusConfig: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-green-500/10 text-green-600' },
  suspended: { label: 'Suspended', color: 'bg-yellow-500/10 text-yellow-600' },
  inactive: { label: 'Inactive', color: 'bg-gray-500/10 text-gray-500' },
};

export function CustomerAccountsTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<CustomerAccount | null>(null);
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);
  const [showTransactionsDialog, setShowTransactionsDialog] = useState(false);

  const { formatCurrency } = useCurrency();
  const { accounts, isLoading, updateAccount, deleteAccount } = useCustomerAccounts(debouncedSearch);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const stats = {
    total: accounts.length,
    active: accounts.filter((a) => a.status === 'active').length,
    totalBalance: accounts.reduce((sum, a) => sum + a.wallet_balance, 0),
  };

  const handleTopUp = (account: CustomerAccount) => {
    setSelectedAccount(account);
    setShowTopUpDialog(true);
  };

  const handleViewTransactions = (account: CustomerAccount) => {
    setSelectedAccount(account);
    setShowTransactionsDialog(true);
  };

  const handleSuspend = async (account: CustomerAccount) => {
    const newStatus = account.status === 'suspended' ? 'active' : 'suspended';
    const action = newStatus === 'suspended' ? 'suspend' : 'reactivate';

    if (!confirm(`Are you sure you want to ${action} this account?`)) return;

    try {
      await updateAccount.mutateAsync({ id: account.id, status: newStatus });
      toast.success('Account Updated', {
        description: `Account has been ${action}d.`,
      });
    } catch (error: any) {
      toast.error('Error', {
        description: error?.message || `Failed to ${action} account`,
      });
    }
  };

  const handleDelete = async (account: CustomerAccount) => {
    const name = `${account.first_name || ''} ${account.last_name || ''}`.trim() || account.username;

    if (!confirm(`Are you sure you want to delete the account for ${name}? This action cannot be undone.`)) {
      return;
    }

    if (account.wallet_balance > 0) {
      if (!confirm(`This account has a balance of ${formatCurrency(account.wallet_balance)}. Are you absolutely sure?`)) {
        return;
      }
    }

    try {
      await deleteAccount.mutateAsync(account.id);
      toast.success('Account Deleted', {
        description: `Account for ${name} has been deleted.`,
      });
    } catch (error: any) {
      toast.error('Error', {
        description: error?.message || 'Failed to delete account',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Accounts</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-green-500/10">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{stats.active}</p>
              <p className="text-xs text-muted-foreground">Active Accounts</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-500/10">
              <Wallet className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{formatCurrency(stats.totalBalance)}</p>
              <p className="text-xs text-muted-foreground">Total Wallet Balance</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by username or customer number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Button variant="blaze" className="gap-2 ml-auto" onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4" />
          Create Account
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Accounts Table */}
      {!isLoading && accounts.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 font-medium text-sm">Customer</th>
                  <th className="text-left p-3 font-medium text-sm">Username</th>
                  <th className="text-left p-3 font-medium text-sm">Customer #</th>
                  <th className="text-left p-3 font-medium text-sm">Status</th>
                  <th className="text-right p-3 font-medium text-sm">Wallet Balance</th>
                  <th className="text-left p-3 font-medium text-sm">Last Login</th>
                  <th className="text-right p-3 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const status = statusConfig[account.status] || statusConfig.inactive;
                  const customerName = `${account.first_name || ''} ${account.last_name || ''}`.trim();

                  return (
                    <tr key={account.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                              {account.first_name?.[0] || account.username[0].toUpperCase()}
                              {account.last_name?.[0] || ''}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm">{customerName || 'No name'}</p>
                            {account.email && (
                              <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                                {account.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                          {account.username}
                        </code>
                      </td>
                      <td className="p-3">
                        <code className="text-sm bg-muted px-1.5 py-0.5 rounded font-mono">
                          {account.customer_number}
                        </code>
                      </td>
                      <td className="p-3">
                        <Badge className={cn('text-xs', status.color)}>
                          {status.label}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        <span className={cn(
                          'font-semibold',
                          account.wallet_balance > 0 ? 'text-green-600' : 'text-muted-foreground'
                        )}>
                          {formatCurrency(account.wallet_balance)}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {account.last_login_at
                          ? new Date(account.last_login_at).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => handleTopUp(account)}
                          >
                            <Wallet className="h-3.5 w-3.5" />
                            Top Up
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleViewTransactions(account)}>
                                <History className="h-4 w-4 mr-2" />
                                View Transactions
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                toast('Coming Soon', {
                                  description: 'Password reset functionality coming soon',
                                });
                              }}>
                                <KeyRound className="h-4 w-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleSuspend(account)}>
                                {account.status === 'suspended' ? (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Reactivate Account
                                  </>
                                ) : (
                                  <>
                                    <Ban className="h-4 w-4 mr-2" />
                                    Suspend Account
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDelete(account)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Account
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && accounts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Wallet className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No customer accounts yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            {searchQuery ? 'Try a different search term' : 'Create accounts for customers to use the self-service kiosk'}
          </p>
          <Button variant="blaze" className="gap-2" onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4" />
            Create Account
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <CreateCustomerAccountDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      <WalletTopUpDialog
        open={showTopUpDialog}
        onOpenChange={setShowTopUpDialog}
        account={selectedAccount}
      />

      <WalletTransactionsDialog
        open={showTransactionsDialog}
        onOpenChange={setShowTransactionsDialog}
        account={selectedAccount}
      />
    </div>
  );
}


















